import { RejectedBannerIcon } from '@/assets/icons/info-icons/rejectedBanner';
import { DeleteBucketIcon } from '@/assets/static/icons/delete-bucket';
import { Banner } from '@/components/basicComponents/banner';
import ModalLoader from '@/components/basicComponents/modal/modalLoader';
import ModalWrappedContent from '@/components/basicComponents/modal/modalWrappedContent';
import Select, { ISelectValue } from '@/components/basicComponents/select';
import { createSimpleSelectOption } from '@/components/basicComponents/select/utils';
import { useErrorMessages } from '@/hooks/i18n';
import { getEnumKeyByValue } from '@/shared/utils/enums';
import { themeVar } from '@/styles/theme';
import { ApolloQueryResult, useMutation, useQuery } from '@apollo/client';
import { ROLE } from '@trace-8/trace8-schema';
import { useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import styled from 'styled-components';
import { usersPageTabs } from '..';
import { DELETE_USER_MUTATION, LIST_ADVISORY_FIRM_USERS_QUERY, LIST_PLATFORM_MANAGER_USERS_QUERY } from '../queries';
import { IUser } from '../types';

interface DeleteUserProps {
  isOpen: boolean;
  activeTab: string;
  currentUser: IUser;
  refetch: () => Promise<ApolloQueryResult<any>>;
  onClose: () => void;
}

const DeleteUser = ({ isOpen, activeTab, currentUser, refetch, onClose }: DeleteUserProps) => {
  const { default: defaultErrorMessage } = useErrorMessages();
  const { control, handleSubmit } = useForm<{ selectedUserId: string }>({ defaultValues: { selectedUserId: undefined } });

  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [selectOptions, setSelectOptions] = useState<ISelectValue[]>([]);
  const [isUserPrimaryAdmin, setIsUserPrimaryAdmin] = useState<boolean>(false);

  const [deleteUser, { loading: deleteUserLoading }] = useMutation(DELETE_USER_MUTATION);

  const { loading: listPlatformManagerUsersLoading } = useQuery<{
    listPlatformManagerUsers: { total: number; page: number; perPage: number; data: IUser[] };
  }>(LIST_PLATFORM_MANAGER_USERS_QUERY, {
    skip: activeTab !== usersPageTabs[0].value || !currentUser?.tenant?.id || !isUserPrimaryAdmin,
    variables: {
      filters: {
        limit: 1000,
        page: 1
      }
    },
    onCompleted: ({ listPlatformManagerUsers }) => {
      setSelectOptions(
        listPlatformManagerUsers.data
          .filter((user) => user.id !== currentUser?.id)
          .map((user) => createSimpleSelectOption(user.id, `${user.firstName} ${user.lastName}`))
      );
    }
  });

  const { loading: listAdvisoryFirmUsersLoading } = useQuery<{
    listAdvisoryFirmUsers: { total: number; page: number; perPage: number; data: IUser[] };
  }>(LIST_ADVISORY_FIRM_USERS_QUERY, {
    skip: activeTab !== usersPageTabs[1].value || !currentUser?.tenant?.id || !isUserPrimaryAdmin,
    variables: {
      filters: {
        tenantId: currentUser?.tenant?.id,
        limit: 1000,
        page: 1
      }
    },
    onCompleted: ({ listAdvisoryFirmUsers }) => {
      setSelectOptions(
        listAdvisoryFirmUsers.data
          .filter((user) => user.id !== currentUser?.id)
          .map((user) => createSimpleSelectOption(user.id, `${user.firstName} ${user.lastName}`))
      );
    }
  });

  useEffect(() => {
    if (!currentUser) return;

    setIsUserPrimaryAdmin(currentUser.roles.includes(getEnumKeyByValue(ROLE, ROLE.PRIMARY_ADMIN)));
  }, [currentUser]);

  const onSave = async ({ selectedUserId }: { selectedUserId: string }) => {
    try {
      setErrorMessage(null);

      await deleteUser({
        variables: {
          data: {
            userId: currentUser.id,
            tenantId: currentUser.tenant.id,
            newDefaultAdvisorUserId: selectedUserId
          }
        }
      });

      refetch();
      onClose();
    } catch (error) {
      console.error(error);
      setErrorMessage(defaultErrorMessage);
    }
  };

  if (!currentUser) return <></>;

  return (
    <ModalWrappedContent
      isOpen={isOpen}
      onClose={onClose}
      confirmButton={{
        name: isUserPrimaryAdmin ? 'Confirm' : 'Delete',
        onClick: handleSubmit(onSave),
        disabled: deleteUserLoading
      }}
      isUseOutsideClick={!deleteUserLoading}
      showCancelButton={!deleteUserLoading}
      showCloseIcon={!deleteUserLoading}
    >
      <ModalBody>
        <DeleteBucketIcon fill={themeVar('font.disabled')} />
        {deleteUserLoading ? (
          <ModalLoader />
        ) : (
          <>
            <ModalTitle>Please confirm you want to delete this user.</ModalTitle>
            {errorMessage && (
              <ErrorBannerWrapper>
                <Banner
                  icon={<RejectedBannerIcon fill={themeVar('context.light')} />}
                  title="Error"
                  description={errorMessage}
                  bgColor={themeVar('context.error')}
                />
              </ErrorBannerWrapper>
            )}
            <ModalDescription>
              {isUserPrimaryAdmin
                ? 'This user is currently the Primary Admin.  Select a new Primary Admin before proceeding.'
                : 'There is no undo for this operation.'}
            </ModalDescription>
            {isUserPrimaryAdmin && (
              <Controller
                name="selectedUserId"
                control={control}
                rules={{
                  required: {
                    value: true,
                    message: 'Required'
                  }
                }}
                render={({ field: { onChange, value }, fieldState: { error } }) => (
                  <Select
                    label="Make one of the remaining active users the Primary Admin"
                    value={value || ''}
                    setValue={onChange}
                    options={selectOptions}
                    errorText={error?.message}
                    isRequiredField
                    loading={listAdvisoryFirmUsersLoading || listPlatformManagerUsersLoading}
                    size="lg"
                  />
                )}
              />
            )}
          </>
        )}
      </ModalBody>
    </ModalWrappedContent>
  );
};

export default DeleteUser;

const ModalBody = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-direction: column;
  gap: 10px;
  margin-top: 10px;
`;

const ModalTitle = styled.div`
  font-family: Blinker;
  font-size: 33px;
  font-style: normal;
  font-weight: 400;
  line-height: 46.2px;
  text-align: center;
  color: ${themeVar('font.strong')};
`;

const ModalDescription = styled.div`
  font-size: 16px;
  text-align: center;
  color: ${themeVar('font.base')};
`;

const ErrorBannerWrapper = styled.div`
  margin-bottom: 10px;
`;
